import { apiFetch } from "./client";

/**
 * Offer pipeline stages. `offer_stage` is a
 * shared configuration catalog, not tenant-scoped — every internal user with
 * offers access can read the full list via GET /api/offer-stages. Admin
 * CRUD/reorder/archive lives under /api/admin/offer-stages.
 */

export type OfferStageKind = "OPEN" | "WON" | "LOST";

export type OfferStage = {
  id: string;
  name: string;
  position: number;
  color: string | null;
  kind: OfferStageKind;
  /** true once archived (soft-delete) — GET /api/offer-stages returns every
   * stage including archived ones, so callers must filter this out for any
   * UI that only shows the live pipeline (kanban columns, filter options). */
  archived: boolean;
  createdAt: string;
  updatedAt: string;
};

export type StageUpsert = {
  name?: string;
  color?: string | null;
};

export const offerStagesApi = {
  list: () => apiFetch<OfferStage[]>("/offer-stages"),

  /** Admin-only (offer_stages:manage). New stages are always minted OPEN — kind isn't settable here. */
  create: (body: { name: string; color?: string | null }) =>
    apiFetch<OfferStage>("/admin/offer-stages", { method: "POST", body }),
  /** Partial update — omitted/undefined fields are left unchanged; kind is never editable. */
  update: (id: string, body: StageUpsert) =>
    apiFetch<OfferStage>(`/admin/offer-stages/${id}`, { method: "PATCH", body }),
  /**
   * Archives ("deletes") a stage. `targetStageId` is required only when the
   * stage still holds offers (backend 409s OFFER_STAGE_TARGET_REQUIRED
   * without it) — the caller re-prompts for a target and retries.
   */
  archive: (id: string, targetStageId?: string | null) =>
    apiFetch<void>(
      `/admin/offer-stages/${id}${targetStageId ? `?targetStageId=${encodeURIComponent(targetStageId)}` : ""}`,
      { method: "DELETE" },
    ),
  /** Full desired ordering of every currently-active stage id (position = 1-based index). */
  reorder: (stageIds: string[]) =>
    apiFetch<OfferStage[]>("/admin/offer-stages/reorder", { method: "POST", body: { stageIds } }),
};
